import { Check, Globe } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { LOCALES, parseLocalizedPath, switchLocalePath, useI18n } from "@/i18n";
import { trackAnalyticsEvent } from "@/lib/analytics";
import { cn } from "@/lib/utils";

export function LanguageSelector({
  onDark = false,
  className,
}: {
  onDark?: boolean;
  className?: string;
}) {
  const { locale, setLocale, t } = useI18n();
  const current = LOCALES.find((l) => l.code === locale) ?? LOCALES[0]!;

  const selectLocale = (nextLocale: (typeof LOCALES)[number]["code"]) => {
    if (nextLocale === locale) return;

    const parsed = parseLocalizedPath(window.location.pathname);
    trackAnalyticsEvent({
      event: "language_change",
      from_locale: locale,
      to_locale: nextLocale,
      route_id: parsed.routeId ?? "unknown",
    });

    setLocale(nextLocale);

    const pathname = switchLocalePath(window.location.pathname, nextLocale) ?? parsed.localizedPath;
    const nextUrl = `${pathname}${window.location.search}${window.location.hash}`;
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;

    if (nextUrl !== currentUrl) window.location.assign(nextUrl);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          aria-label={t.nav.language}
          className={cn(
            "gap-1.5 rounded-full px-3",
            onDark
              ? "text-primary-foreground/80 hover:bg-primary-foreground/10 hover:text-primary-foreground"
              : "text-muted-foreground hover:text-foreground",
            className,
          )}
        >
          <Globe aria-hidden="true" />
          <span className="text-xs font-medium tracking-wide">{current.short}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-44">
        {LOCALES.map((l) => (
          <DropdownMenuItem
            key={l.code}
            onSelect={() => selectLocale(l.code)}
            className="justify-between gap-4"
          >
            <span>{l.label}</span>
            {l.code === locale ? <Check className="size-3.5" aria-hidden="true" /> : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
